/**
 * Global thumbnail cache service
 * Persists across component instances to avoid duplicate API calls
 *
 * Signed URLs are cached per asset id. When an asset's thumbnail changes
 * (ND-2415: a user override is set or reset) call invalidate(id): it drops the
 * cached URL and bumps a reactive version, so every mounted view watching
 * version(id) reloads its thumbnail without a page reload.
 */
import { reactive } from 'vue'

const thumbnailCache = new Map()
const versions = reactive({})

// Ids arrive as numbers or strings depending on the caller.
const key = (assetId) => String(assetId)

export default {
  /**
   * Get cached thumbnail URL
   * @param {string} assetId - Media asset ID
   * @returns {string|null} Cached URL or null if not cached
   */
  get(assetId) {
    return thumbnailCache.get(key(assetId)) || null
  },

  /**
   * Set thumbnail URL in cache
   * @param {string} assetId - Media asset ID
   * @param {string} signedUrl - Signed URL from backend
   */
  set(assetId, signedUrl) {
    thumbnailCache.set(key(assetId), signedUrl)
  },

  /**
   * Check if asset is cached
   * @param {string} assetId - Media asset ID
   * @returns {boolean}
   */
  has(assetId) {
    return thumbnailCache.has(key(assetId))
  },

  /**
   * Clear all cached thumbnails
   */
  clear() {
    thumbnailCache.clear()
  },

  /**
   * Clear a specific asset from cache
   * @param {string} assetId - Media asset ID
   */
  remove(assetId) {
    thumbnailCache.delete(key(assetId))
  },

  /**
   * The asset's thumbnail changed: drop the cached URL and notify every view
   * watching version(assetId).
   * @param {string} assetId - Media asset ID
   */
  invalidate(assetId) {
    if (assetId === null || assetId === undefined) return
    thumbnailCache.delete(key(assetId))
    versions[key(assetId)] = (versions[key(assetId)] ?? 0) + 1
  },

  /**
   * Reactive counter that changes whenever invalidate(assetId) runs. Watch it
   * (from a computed or watch source) to reload a thumbnail that changed.
   * @param {string} assetId - Media asset ID
   * @returns {number}
   */
  version(assetId) {
    if (assetId === null || assetId === undefined) return 0
    return versions[key(assetId)] ?? 0
  },

  /**
   * Get cache size
   * @returns {number} Number of cached items
   */
  size() {
    return thumbnailCache.size
  }
}
